
import { useRef, useState } from 'react';
import { Bell, BellOff, Link2, Play, Star, Undo2 } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  confirmDialog,
  toast,
} from '@/shared/ui';
import { apiErrorText } from '@/shared/api';
import { ticketsApi } from '@/shared/api/tickets';
import { WITHDRAWABLE_STATUSES } from '@/shared/config';
import type { Ticket } from '@/entities/ticket';
import {
  useMuteStore,
  useTicketMute,
  useTicketMuteBusy,
} from '@/features/notifications/model/mute-store';
import { useTicketsStore } from '../model/tickets-store';
import { ResolutionDecisionDialog } from './ResolutionDecisionDialog';

export interface TicketContextMenuProps {
  ticket: Ticket;
  currentUserId?: string;
  onTake?: (id: string) => void;
  children: React.ReactElement;
}

function ticketUrl(id: string): string {
  return `${window.location.origin}/tickets/view/${id}`;
}

async function copyLink(id: string) {
  const url = ticketUrl(id);
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Ссылка на заявку скопирована');
  } catch {
    toast.error(`Не удалось скопировать. Ссылка: ${url}`);
  }
}

export const TicketContextMenu: React.FC<TicketContextMenuProps> = ({
  ticket,
  currentUserId,
  onTake,
  children,
}) => {
  const loadMute = useMuteStore((s) => s.load);
  const toggleMuteInStore = useMuteStore((s) => s.toggle);
  const muteEntry = useTicketMute(ticket.id);
  const muteBusy = useTicketMuteBusy(ticket.id);
  const muted = muteEntry ? muteEntry.muted : (ticket.muted ?? null);
  const canMute = muteEntry
    ? muteEntry.canMute
    : Boolean(currentUserId) &&
      (ticket.creatorId === currentUserId ||
        (ticket.assignees ?? []).some((a) => a.userId === currentUserId));
  const [rateOpen, setRateOpen] = useState(false);
  const applyTicketUpdate = useTicketsStore((s) => s.applyTicketUpdate);

  const isOwner = Boolean(currentUserId) && ticket.creatorId === currentUserId;
  const canWithdraw = isOwner && WITHDRAWABLE_STATUSES.includes(ticket.status);
  const canRate = isOwner && Boolean(ticket.awaitsRequesterDecision);
  const isAssignedToMe =
    Boolean(currentUserId) && (ticket.assignees ?? []).some((a) => a.userId === currentUserId);
  const canTake =
    Boolean(onTake) &&
    !isAssignedToMe &&
    !['withdrawn', 'closed', 'resolved', 'not_confirmed', 'rejected'].includes(ticket.status);

  const afterCloseRef = useRef<(() => void) | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (!open) return;
    afterCloseRef.current = null;
    void loadMute(ticket.id);
  };

  const handleOpenChangeComplete = (open: boolean) => {
    if (open) return;
    const run = afterCloseRef.current;
    afterCloseRef.current = null;
    run?.();
  };

  const toggleMute = async () => {
    const next = await toggleMuteInStore(ticket.id);
    if (next == null) return;
    toast.success(next ? 'Уведомления по заявке приглушены' : 'Уведомления по заявке включены');
  };

  const withdraw = async () => {
    const ok = await confirmDialog({
      title: 'Отозвать заявку?',
      description:
        'Заявка перейдёт в статус «Отозвана»: работа по ней прекратится, вернуть её будет нельзя.',
      confirmLabel: 'Отозвать',
      danger: true,
    });
    if (!ok) return;
    try {
      const res = await ticketsApi.withdrawTicket(ticket.id);
      if (res.data) applyTicketUpdate({ ...res.data, id: ticket.id });
      toast.success('Заявка отозвана');
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отозвать заявку'));
    }
  };

  return (
    <>
      <ContextMenu onOpenChange={handleOpenChange} onOpenChangeComplete={handleOpenChangeComplete}>
        <ContextMenuTrigger render={children} />
        <ContextMenuContent>
          {canTake && (
            <>
              <ContextMenuItem onClick={() => onTake?.(ticket.id)}>
                <Play size={14} aria-hidden /> Взять в работу
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}

          { }
          {canMute && (
            <ContextMenuItem
              disabled={muted == null || muteBusy}
              onClick={() => void toggleMute()}
            >
              {muted ? <Bell size={14} aria-hidden /> : <BellOff size={14} aria-hidden />}
              {muted === null
                ? 'Уведомления…'
                : muted
                  ? 'Включить уведомления'
                  : 'Выключить уведомления'}
            </ContextMenuItem>
          )}

          {canRate && (
            <ContextMenuItem
              onClick={() => {
                afterCloseRef.current = () => setRateOpen(true);
              }}
            >
              <Star size={14} aria-hidden /> Оценить выполнение
            </ContextMenuItem>
          )}

          <ContextMenuItem onClick={() => void copyLink(ticket.id)}>
            <Link2 size={14} aria-hidden /> Скопировать ссылку
          </ContextMenuItem>

          {canWithdraw && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                variant="danger"
                onClick={() => {
                  afterCloseRef.current = () => void withdraw();
                }}
              >
                <Undo2 size={14} aria-hidden /> Отозвать заявку
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      { }
      {rateOpen && (
        <ResolutionDecisionDialog
          ticketId={ticket.id}
          mode="confirm"
          onClose={() => setRateOpen(false)}
          onDone={() => {
            setRateOpen(false);
          }}
        />
      )}
    </>
  );
};
